import { useMemo } from "react";
import { Ledger } from "@common/contracts/ledger.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    flattenLedgersByEntity,
    uniqueLedgersByBasisAndName,
} from "@lib/ledgerViewColumns";

interface UseMultiLedgerColumnsOptions {
    basis?: AccountingLedgerBasis;
    uniqueByBasisAndName?: boolean;
}

export function useMultiLedgerColumns(
    entityIds: number[],
    { basis, uniqueByBasisAndName = false }: UseMultiLedgerColumnsOptions = {},
): Ledger[] {
    const { multiLedger } = useTypedFlags();
    const { data: ledgersByEntityId } = useLedgersByEntityIds(entityIds);

    return useMemo(() => {
        if (!multiLedger) {
            return [];
        }

        const ledgers = flattenLedgersByEntity(ledgersByEntityId).filter(
            (ledger) => !basis || ledger.basis === basis,
        );

        return uniqueByBasisAndName
            ? uniqueLedgersByBasisAndName(ledgers)
            : ledgers;
    }, [basis, ledgersByEntityId, multiLedger, uniqueByBasisAndName]);
}
